import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'lab_client';

/**
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatLabErrors = errPayload => {
  if (errPayload == null) {
    return ['Request failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  if (errPayload.detail) {
    return [String(errPayload.detail)];
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => messages.push(`${key}: ${v}`));
    } else {
      messages.push(String(value));
    }
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

/**
 * @typedef {Object} StockOpenNotifyUser
 * @property {number} id
 * @property {string} [first_name]
 * @property {string} [last_name]
 * @property {string} [email]
 */

/**
 * @typedef {Object} CurrentLab
 * @property {number} id
 * @property {string} name
 * @property {string} slug
 * @property {string} [logo_url]
 * @property {StockOpenNotifyUser|null} [stock_open_notify_user]
 */

/**
 * GET /v1/labs/current/ — current lab tenant for the authenticated lab user.
 *
 * @async
 * @param {string} token
 * @returns {Promise<{ success: boolean, data?: CurrentLab, messages?: string[], status?: number }>}
 */
export const getCurrentLab = async token => {
  try {
    logInfo(LOG_CONTEXT, 'getCurrentLab start');
    const response = await axios.get(`${BACKEND_URL}labs/current/`, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'getCurrentLab success', {
      labId: response.data?.id,
      hasNotifyUser: Boolean(response.data?.stock_open_notify_user),
    });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'getCurrentLab failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * Build a PATCH body for labs/current from a partial fields object.
 * Includes `stock_open_notify_user_id` when the key is present (including null to clear).
 *
 * @param {Object} fields
 * @param {string} [fields.name]
 * @param {number|null} [fields.stock_open_notify_user_id]
 * @returns {Object}
 */
export const buildLabPatchBody = fields => {
  const body = {};
  if (fields == null || typeof fields !== 'object') {
    return body;
  }
  if (Object.prototype.hasOwnProperty.call(fields, 'name')) {
    body.name = String(fields.name ?? '').trim();
  }
  if (Object.prototype.hasOwnProperty.call(fields, 'stock_open_notify_user_id')) {
    const raw = fields.stock_open_notify_user_id;
    body.stock_open_notify_user_id =
      raw == null || raw === '' ? null : Number(raw);
  }
  return body;
};

/**
 * PATCH /v1/labs/current/ — partial update (Admin only).
 * Supports `name` and/or `stock_open_notify_user_id` (null clears the digest recipient).
 *
 * @async
 * @param {string} token
 * @param {{ name?: string, stock_open_notify_user_id?: number|null }} fields
 * @returns {Promise<{ success: boolean, data?: CurrentLab, messages?: string[], status?: number }>}
 */
export const patchCurrentLab = async (token, fields) => {
  const body = buildLabPatchBody(fields);
  try {
    logInfo(LOG_CONTEXT, 'patchCurrentLab start', {
      keys: Object.keys(body),
      hasNotifyUserId: Object.prototype.hasOwnProperty.call(
        body,
        'stock_open_notify_user_id'
      ),
      notifyUserId: body.stock_open_notify_user_id ?? null,
    });
    const response = await axios.patch(`${BACKEND_URL}labs/current/`, body, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    });
    logInfo(LOG_CONTEXT, 'patchCurrentLab success', {
      labId: response.data?.id,
      hasNotifyUser: Boolean(response.data?.stock_open_notify_user),
    });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'patchCurrentLab failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * PATCH /v1/labs/current/ — rename lab display name (Admin only).
 * Thin wrapper around {@link patchCurrentLab}.
 *
 * @async
 * @param {string} token
 * @param {string} name - Trimmed before send; only `name` is sent.
 * @returns {Promise<{ success: boolean, data?: CurrentLab, messages?: string[], status?: number }>}
 */
export const renameCurrentLab = async (token, name) => {
  const trimmedName = String(name ?? '').trim();
  logInfo(LOG_CONTEXT, 'renameCurrentLab start', {
    nameLength: trimmedName.length,
  });
  return patchCurrentLab(token, { name: trimmedName });
};

/**
 * POST /v1/labs/current/logo/ — upload or replace lab logo (Admin only).
 *
 * @async
 * @param {string} token
 * @param {File} file - Image file (field name `logo`).
 * @returns {Promise<{ success: boolean, data?: Object, messages?: string[], status?: number }>}
 */
export const uploadLabLogo = async (token, file) => {
  const formData = new FormData();
  formData.append('logo', file);
  try {
    logInfo(LOG_CONTEXT, 'uploadLabLogo start', {
      fileName: file?.name,
      fileSize: file?.size,
    });
    const response = await axios.post(`${BACKEND_URL}labs/current/logo/`, formData, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'uploadLabLogo success', { labId: response.data?.id });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'uploadLabLogo failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * DELETE /v1/labs/current/logo/ — remove lab logo (Admin only).
 *
 * @async
 * @param {string} token
 * @returns {Promise<{ success: boolean, data?: Object, messages?: string[], status?: number }>}
 */
export const deleteLabLogo = async token => {
  try {
    logInfo(LOG_CONTEXT, 'deleteLabLogo start');
    const response = await axios.delete(`${BACKEND_URL}labs/current/logo/`, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'deleteLabLogo success', { labId: response.data?.id });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'deleteLabLogo failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};
